<template>
  <div class="quote-round flex-col">
    <template v-if="roundQuoting">
      <div class="tip">
        未到达报价截止时间，价格分评审无法进行，请到达报价截止时间后进行评审
      </div>
      <div class="round-info flex-1 flex-col">
        <div class="round-info-time">本轮报价截止时间：{{ offerEndTime }}</div>
        <tab-switch
          :default-tab-index="defaultTabIndex"
          :tab-list="roundQuoteTabList"
          usually-color="#666"
          @tabChange="changeQuoteRoundTab"
        >
          <template v-slot:content="{ tab }">
            {{ tab.label }}({{ tab.count }})
          </template>
        </tab-switch>
        <el-scrollbar class="scrollbar-y-hidden m-t-24">
          <jczh-table-content
            :data="list"
            :option="listOption"
            pagination
            :page.sync="page"
            :limit.sync="size"
            :total="total"
          />
        </el-scrollbar>
        <div class="round-action">
          <el-button
            type="primary"
            :disabled="!canEndRound"
            @click="endInquiryQuoteRound"
          >
            结束本轮报价
          </el-button>
        </div>
      </div>
    </template>
    <div v-else-if="inQuotedWithOpenSeal">
      <div class="tip">
        当前项目比价方式为密封比价，需开启报价后进行评审
      </div>
      <el-button class="m-t-24" type="primary" @click="openSealQuote">
        开启报价
      </el-button>
    </div>
  </div>
</template>

<script>
import TabSwitch from '@/components/common/TabSwitch.vue'
import {
  endInquiryReviewQuoteRoundApi,
  getInquiryReviewQuoteRoundSupplierListApi,
  getInquiryReviewQuoteRoundTotalApi
} from '@/api/procure/inquiry-review'
import { asyncInterval } from '@/utils/utils'
import { openInquiryOfferApi } from '@/api/procure/inquiry'

/**
 * 询比多轮报价
 */
export default {
  name: 'InquiryReviewQuoteRound',

  components: { TabSwitch },

  props: {
    reviewId: {
      type: [String, Number],
      default: ''
    },

    orderId: {
      type: [String, Number],
      default: ''
    },

    roundId: {
      type: [String, Number],
      default: ''
    },

    // 是否多轮报价中
    roundQuoting: {
      type: Boolean,
      default: false
    },

    // 多轮报价已截止
    roundQuoteEnd: {
      type: Boolean,
      default: false
    },

    // 密封多轮报价已截止
    inQuotedWithOpenSeal: {
      type: Boolean,
      default: false
    },

    // 报价截止时间
    offerEndTime: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      // 多轮报价统计
      roundQuoteStatistic: {
        UN_QUOTE: { label: '未报价单位', count: 0, isSubmitOffer: 0 },
        QUOTED: { label: '已报价单位', count: 0, isSubmitOffer: 1 }
      },
      roundQuoteTab: 0,

      loopQuoteRoundControl: null,
      list: [],
      listOption: [{ label: '报价单位', prop: 'supplierName' }],
      page: 1,
      size: 10,
      total: 0
    }
  },

  computed: {
    roundQuoteTabList() {
      return Object.values(this.roundQuoteStatistic)
    },

    // 能提前结束本轮报价
    canEndRound() {
      // 当所有报价单位都已经提交报价，且轮次仍然在报价中
      return this.roundQuoteStatistic.UN_QUOTE.count === 0 && this.roundQuoting
    },

    defaultTabIndex() {
      if (!this.roundQuoteStatistic.UN_QUOTE.count) {
        return this.roundQuoteTabList.findIndex(
          item => item.label === this.roundQuoteStatistic.QUOTED.label
        )
      }
      return 0
    }
  },

  watch: {
    roundQuoting: {
      handler(val) {
        if (val) this.loopQuoteRound()
      },
      immediate: true
    },

    roundQuoteEnd: {
      handler(val) {
        if (val) this.loopQuoteRoundControl.stop()
      },
      immediate: true
    }
  },

  beforeDestroy() {
    if (this.loopQuoteRoundControl) {
      this.loopQuoteRoundControl.stop()
    }
  },

  methods: {
    /**
     * 开启密封报价
     */
    openSealQuote() {
      this.$systemConfirm('是否确定开启报价').then(result => {
        if (!result) return
        openInquiryOfferApi(this.orderId, this.roundId)
          .then(res => {
            this.$emit('refresh')
          })
          .catch(error => {
            this.$message.error(error.message)
          })
      })
    },

    /**
     * 轮询本轮报价信息
     */
    loopQuoteRound() {
      if (this.roundQuoteEnd && this.loopQuoteRoundControl) {
        this.loopQuoteRoundControl.stop()
        return
      }
      if (this.loopQuoteRoundControl) {
        this.loopQuoteRoundControl.stop()
      }
      this.loopQuoteRoundControl = asyncInterval(this.getInquiryQuoteRoundPage)
      this.loopQuoteRoundControl.start()
    },

    /**
     * 获取当前轮次报价供应商统计
     */
    getInquiryQuoteRoundTotal() {
      getInquiryReviewQuoteRoundTotalApi(this.reviewId, {
        orderId: this.orderId
      })
        .then(res => {
          const _data = res.data || {}
          this.roundQuoteStatistic.UN_QUOTE.count = _data.notSubmitCnt || 0
          this.roundQuoteStatistic.QUOTED.count = _data.submitCnt || 0
          // 当全部供应商已报价，则停止轮询
          if (!_data.notSubmitCnt) {
            this.loopQuoteRoundControl.stop()
          }
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 获取当前轮次报价供应商列表
     */
    getInquiryQuoteRoundPage() {
      const params = {
        orderId: this.orderId,
        isSubmitOffer: this.roundQuoteTab,
        page: this.page,
        size: this.size
      }
      getInquiryReviewQuoteRoundSupplierListApi(this.reviewId, params)
        .then(res => {
          this.list = res.data?.records || []
          this.total = res.data?.total || 0
          this.getInquiryQuoteRoundTotal()
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 提前结束当论报价
     */
    endInquiryQuoteRound() {
      if (!this.canEndRound) {
        this.$message.warning('等待全部单位报价后才能结束本轮报价')
        return
      }
      if (this.canEndRound) {
        endInquiryReviewQuoteRoundApi(this.reviewId, { orderId: this.orderId })
          .then(res => {
            this.$emit('refresh')
          })
          .catch(err => {
            if (err?.message) this.$message.error(err.message)
          })
      }
    },

    changeQuoteRoundTab(index) {
      this.roundQuoteTab = this.roundQuoteTabList[index]?.isSubmitOffer
      this.loopQuoteRound()
    }
  }
}
</script>

<style scoped lang="scss">
.quote-round {
  background: $white-color;
  padding-top: 64px;
  text-align: center;
  overflow: hidden;

  .tip {
    font-size: 18px;
    text-align: center;
  }
}

.round-info {
  text-align: left;
  padding: 48px;
  overflow: hidden;

  .round-info-time {
    font-size: 16px;
    @include text-bold;
    margin-bottom: 24px;
  }

  .round-action {
    text-align: center;
    margin-top: 24px;
  }
}
</style>
